import { useEffect, useState, useRef } from "react";
import { Play, X, ChevronLeft, ChevronRight, Sparkles, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const mediaSrc = (url) => (url?.startsWith("http") ? url : `${BACKEND_URL}${url}`);

const LightboxImages = ({ items, startIndex = 0, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [items.length, onClose]);
  const cur = items[idx];
  return (
    <div className="fixed inset-0 z-[80] bg-[#0a0a0a]/97 backdrop-blur-md flex items-center justify-center p-6" data-testid="ref-lightbox">
      <button onClick={onClose} className="absolute top-6 right-6 h-12 w-12 inline-flex items-center justify-center border border-[#d4af37]/40 hover:border-[#d4af37] text-white hover:text-[#d4af37]" data-testid="ref-lightbox-close">
        <X className="h-5 w-5" />
      </button>
      {items.length > 1 && (
        <>
          <button onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)} className="absolute left-6 h-12 w-12 inline-flex items-center justify-center border border-[#d4af37]/40 hover:border-[#d4af37] text-white hover:text-[#d4af37]" aria-label="Önceki">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button onClick={() => setIdx((i) => (i + 1) % items.length)} className="absolute right-6 h-12 w-12 inline-flex items-center justify-center border border-[#d4af37]/40 hover:border-[#d4af37] text-white hover:text-[#d4af37]" aria-label="Sonraki">
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}
      <div className="max-w-6xl max-h-[85vh] w-full flex items-center justify-center">
        {cur.kind === "video" ? (
          <video src={mediaSrc(cur.url)} controls autoPlay className="max-h-[85vh] max-w-full" />
        ) : (
          <img src={mediaSrc(cur.url)} alt="" className="max-h-[85vh] max-w-full object-contain" />
        )}
      </div>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/55">{idx + 1} / {items.length}</div>
    </div>
  );
};

const VideoModal = ({ items, onClose }) => {
  const videos = items.filter((m) => m.kind === "video");
  const photos = items.filter((m) => m.kind === "image");
  const [tab, setTab] = useState("video");
  const [photoIdx, setPhotoIdx] = useState(0);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[80] bg-[#0a0a0a]/97 backdrop-blur-md p-4 md:p-10 overflow-y-auto" data-testid="ref-video-modal">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <button onClick={() => setTab("video")} className={`px-4 py-2 font-display uppercase tracking-[0.2em] text-xs border ${tab === "video" ? "border-[#d4af37] text-[#d4af37]" : "border-white/10 text-white/55"}`}>Video</button>
            {photos.length > 0 && <button onClick={() => setTab("photos")} className={`px-4 py-2 font-display uppercase tracking-[0.2em] text-xs border ${tab === "photos" ? "border-[#d4af37] text-[#d4af37]" : "border-white/10 text-white/55"}`}>Fotoğraflar ({photos.length})</button>}
          </div>
          <button onClick={onClose} className="h-12 w-12 inline-flex items-center justify-center border border-[#d4af37]/40 hover:border-[#d4af37] text-white hover:text-[#d4af37]"><X className="h-5 w-5" /></button>
        </div>
        {tab === "video" ? (
          <video src={mediaSrc(videos[0]?.url)} controls autoPlay className="w-full max-h-[78vh] bg-black" />
        ) : (
          <div>
            <img src={mediaSrc(photos[photoIdx].url)} alt="" className="w-full max-h-[70vh] object-contain bg-black" />
            <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
              {photos.map((p, i) => (
                <button key={i} onClick={() => setPhotoIdx(i)} className={`h-16 w-16 overflow-hidden border ${i === photoIdx ? "border-[#d4af37]" : "border-white/10"}`}>
                  <img src={mediaSrc(p.url)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CarouselCard = ({ ref: r, onOpen }) => {
  const [idx, setIdx] = useState(0);
  const next = (e) => { e.stopPropagation(); setIdx((i) => (i + 1) % r.media.length); };
  const prev = (e) => { e.stopPropagation(); setIdx((i) => (i - 1 + r.media.length) % r.media.length); };
  const cur = r.media[idx];
  return (
    <article
      onClick={() => onOpen(r, idx)}
      data-testid={`ref-card-${r.id}`}
      className="group relative aspect-[4/3] overflow-hidden bg-[#0a0a0a] ring-1 ring-white/8 hover:ring-[#d4af37]/60 transition cursor-pointer"
    >
      <div className="absolute inset-0">
        {cur.kind === "video" ? (
          <video src={mediaSrc(cur.url)} muted playsInline className="w-full h-full object-cover" />
        ) : (
          <img src={mediaSrc(cur.url)} alt={r.title} loading="lazy" className="w-full h-full object-cover scale-105 group-hover:scale-110 transition duration-700" />
        )}
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent" />
      {r.media.length > 1 && (
        <>
          <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 bg-[#0a0a0a]/70 border border-white/15 hover:border-[#d4af37] text-white inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 bg-[#0a0a0a]/70 border border-white/15 hover:border-[#d4af37] text-white inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="absolute top-3 right-3 font-mono text-[10px] uppercase tracking-[0.25em] bg-[#0a0a0a]/70 border border-white/15 text-white px-2 py-1">
            {idx + 1}/{r.media.length}
          </div>
        </>
      )}
      {cur.kind === "video" && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="h-16 w-16 rounded-full bg-[#d4af37] text-[#121212] flex items-center justify-center group-hover:scale-110 transition">
            <Play className="h-6 w-6 ml-1" fill="currentColor" />
          </div>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 p-5">
        {r.service_type && <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">{r.service_type}</div>}
        <div className="font-display text-2xl md:text-3xl mt-1 text-white">{r.title}</div>
        {r.description && <div className="text-white/55 text-sm mt-1 line-clamp-2">{r.description}</div>}
      </div>
    </article>
  );
};

const SingleCard = ({ ref: r, onOpen }) => {
  const m = r.media[0];
  return (
    <article
      onClick={() => onOpen(r, 0)}
      data-testid={`ref-card-${r.id}`}
      className="group relative aspect-[4/3] overflow-hidden bg-[#0a0a0a] ring-1 ring-white/8 hover:ring-[#d4af37]/60 transition cursor-pointer"
    >
      {m.kind === "video" ? (
        <video src={mediaSrc(m.url)} muted playsInline className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition duration-700" />
      ) : (
        <img src={mediaSrc(m.url)} alt={r.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition duration-700" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent" />
      {m.kind === "video" && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="h-16 w-16 rounded-full bg-[#d4af37] text-[#121212] flex items-center justify-center group-hover:scale-110 transition">
            <Play className="h-6 w-6 ml-1" fill="currentColor" />
          </div>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 p-5">
        {r.service_type && <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">{r.service_type}</div>}
        <div className="font-display text-2xl md:text-3xl mt-1 text-white">{r.title}</div>
        {r.description && <div className="text-white/55 text-sm mt-1 line-clamp-2">{r.description}</div>}
      </div>
    </article>
  );
};

const NameMarquee = ({ items }) => {
  if (items.length === 0) return null;
  const loop = [...items, ...items];
  return (
    <div className="relative overflow-hidden border-y border-[#d4af37]/15 py-8 bg-[#0a0a0a]" data-testid="ref-marquee">
      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
      <div className="flex gap-12 marquee-track whitespace-nowrap">
        {loop.map((r, i) => (
          <div key={`${r.id}-${i}`} className="inline-flex items-center gap-4 px-6">
            <Sparkles className="h-4 w-4 text-[#d4af37] shrink-0" />
            <span className="font-display text-2xl md:text-4xl tracking-tight text-white whitespace-nowrap">{r.title}</span>
            {r.service_type && <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#d4af37]/80">· {r.service_type}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function ReferencesShowcase() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    api.get("/references")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const noMedia = items.filter((r) => !r.has_media || r.media.length === 0);
  const withMedia = items.filter((r) => r.has_media && r.media.length > 0);

  const openItem = (r, startIdx = 0) => {
    const hasVideo = r.media.some((m) => m.kind === "video");
    setOpen({ ref: r, startIdx, hasVideo });
  };

  return (
    <section id="referanslar" ref={sectionRef} className="relative px-6 md:px-12 py-24 md:py-32 grain" data-testid="references-section">
      <div className="pointer-events-none absolute -top-24 left-1/4 h-[420px] w-[420px] rounded-full bg-[#d4af37]/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-10 h-[320px] w-[320px] rounded-full bg-[#d4af37]/5 blur-3xl" />

      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
        <div className="lg:col-span-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#d4af37]">— Referanslar</p>
          <h2 className="mt-4 font-display font-extrabold tracking-tighter text-5xl md:text-7xl lg:text-[7rem] leading-[0.85] text-balance">
            Güveni inşa ettiğimiz
            <br />
            <span className="gold-shimmer">imzalar.</span>
          </h2>
        </div>
        <p className="lg:col-span-4 text-white/55 text-base md:text-lg leading-relaxed">
          Eskişehir&apos;in en seçkin bina, kurum ve aile projelerinde yer aldık. Her referans, bir güven mührüdür.
        </p>
      </div>

      {loading ? (
        <div className="font-mono text-xs text-white/40">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/10 p-14 text-center text-white/45 font-mono text-xs uppercase tracking-[0.3em]" data-testid="ref-empty">
          Yakında referanslarımızı burada paylaşacağız
        </div>
      ) : (
        <div className="space-y-14 relative">
          {withMedia.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {withMedia.map((r) =>
                r.media.length > 1 ? (
                  <CarouselCard key={r.id} ref={r} onOpen={openItem} />
                ) : (
                  <SingleCard key={r.id} ref={r} onOpen={openItem} />
                )
              )}
            </div>
          )}

          {noMedia.length > 0 && (
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#d4af37] mb-4">Kurumsal Referanslar</p>
              <NameMarquee items={noMedia} />
            </div>
          )}
        </div>
      )}

      {open && (open.hasVideo ? (
        <VideoModal items={open.ref.media} onClose={() => setOpen(null)} />
      ) : (
        <LightboxImages items={open.ref.media} startIndex={open.startIdx} onClose={() => setOpen(null)} />
      ))}
    </section>
  );
}
